import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const GUEST_CART_KEY = 'zelina_guest_cart';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [cartLoading, setCartLoading] = useState(true);
  const [discount, setDiscount] = useState(null);
  const { user, api } = useAuth();

  // ── Load cart based on auth state (merge guest → server on login) ──
  useEffect(() => {
    if (user) {
      mergeGuestCartAndFetch();
    } else {
      loadGuestCart();
    }
  }, [user]);

  const mergeGuestCartAndFetch = async () => {
    setCartLoading(true);
    try {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      const guestItems = stored ? JSON.parse(stored) : [];
      // Merge each guest item into server cart
      for (const item of guestItems) {
        try {
          if (item.product?.texture_variant && item.product?.length && item.product?.color_code && item.product?.color_name) {
            await api.post('/api/catalog/cart/add', {
              texture_variant: item.product.texture_variant,
              length: item.product.length,
              color_code: item.product.color_code,
              color_name: item.product.color_name,
              quantity: item.quantity,
            });
          } else if (item.product_id) {
            await api.post('/api/cart/add', { product_id: item.product_id, quantity: item.quantity });
          }
        } catch (e) {
          // Skip items that fail to merge (e.g. duplicates handled server-side)
        }
      }
      if (guestItems.length > 0) {
        localStorage.removeItem(GUEST_CART_KEY);
      }
    } catch (e) {
      // Merge failed — just fetch server cart
    }
    await fetchCart();
  };

  // ── Server cart (authenticated) ──
  const fetchCart = async (silent = false) => {
    if (!silent) setCartLoading(true);
    try {
      const { data } = await api.get('/api/cart');
      setCart(data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      if (!silent) setCartLoading(false);
    }
  };

  // ── Guest cart (localStorage) ──
  const loadGuestCart = () => {
    try {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      if (stored) {
        const items = JSON.parse(stored);
        const total = items.reduce((sum, i) => sum + (i.item_total || 0), 0);
        setCart({ items, total });
      } else {
        setCart({ items: [], total: 0 });
      }
    } catch {
      setCart({ items: [], total: 0 });
    }
    setCartLoading(false);
  };

  const saveGuestCart = (items) => {
    const total = items.reduce((sum, i) => sum + (i.item_total || 0), 0);
    setCart({ items, total });
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  };

  const clearGuestCart = () => {
    localStorage.removeItem(GUEST_CART_KEY);
    setCart({ items: [], total: 0 });
  };

  // ── Add to cart (works for both auth + guest) ──
const MAX_QTY_PER_SKU = 10;

  const addToCart = async (productId, quantity = 1, productDetails = null) => {
    if (user) {
      try {
        // Check existing qty
        const existing = cart.items.find(i => i.product_id === productId);
        const currentQty = existing ? existing.quantity : 0;
        if (currentQty + quantity > MAX_QTY_PER_SKU) {
          return { blocked: true };
        }
        await api.post('/api/cart/add', { product_id: productId, quantity });
        await fetchCart();
      } catch (error) {
        throw error;
      }
    } else {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      const items = stored ? JSON.parse(stored) : [];
      const existing = items.find(i => i.product_id === productId);
      if (existing) {
        if (existing.quantity + quantity > MAX_QTY_PER_SKU) {
          return { blocked: true };
        }
        existing.quantity += quantity;
        existing.item_total = existing.quantity * existing.product.price_usd;
      } else {
        items.push({
          id: `guest_${Date.now()}`,
          product_id: productId,
          quantity,
          product: productDetails || { id: productId, name: productId, price_usd: 0, image: null },
          item_total: quantity * (productDetails?.price_usd || 0),
        });
      }
      saveGuestCart(items);
    }
  };

  const removeFromCart = async (cartItemId) => {
    if (user) {
      try {
        await api.delete(`/api/cart/${cartItemId}`);
        await fetchCart();
      } catch (error) {
        console.error('Failed to remove from cart:', error);
      }
    } else {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      const items = stored ? JSON.parse(stored) : [];
      saveGuestCart(items.filter(i => i.id !== cartItemId));
    }
  };

  const updateCartQty = async (cartItemId, quantity) => {
    if (quantity > MAX_QTY_PER_SKU) return { blocked: true };
    if (user) {
      setCart(prev => {
        const newItems = prev.items.map(item => {
          if (item.id === cartItemId) {
            return { ...item, quantity, item_total: quantity * item.product.price_usd };
          }
          return item;
        });
        return { ...prev, items: newItems, total: newItems.reduce((s, i) => s + i.item_total, 0) };
      });
      try {
        await api.patch(`/api/cart/${cartItemId}?quantity=${quantity}`);
        await fetchCart(true);
      } catch (error) {
        console.error('Failed to update cart quantity:', error);
        await fetchCart(true);
      }
    } else {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      const items = stored ? JSON.parse(stored) : [];
      const item = items.find(i => i.id === cartItemId);
      if (item) {
        item.quantity = quantity;
        item.item_total = quantity * item.product.price_usd;
      }
      saveGuestCart(items);
    }
  };

  const applyDiscount = async (code) => {
    try {
      const endpoint = user ? '/api/user/cart/apply-discount' : '/api/user/cart/validate-discount';
      const { data } = await api.post(endpoint, {
        code, cart_total: cart.total,
      });
      setDiscount(data);
      return data;
    } catch (error) {
      setDiscount(null);
      throw error;
    }
  };

  const clearDiscount = () => setDiscount(null);

  const refreshCart = useCallback(() => {
    if (user) {
      fetchCart();
    } else {
      clearGuestCart();
    }
  }, [user]);

  return (
    <CartContext.Provider value={{
      cart, cartLoading, addToCart, removeFromCart, updateCartQty, fetchCart, refreshCart,
      discount, applyDiscount, clearDiscount, isGuest: !user, MAX_QTY_PER_SKU,
    }}>
      {children}
    </CartContext.Provider>
  );
};
